import type { InvitationChannelMode } from '@/stores/invitation-store';

/**
 * What the mono affiliation recipient + integrations allow on the channel step.
 * The shareable link is always available and is not represented here (it is the
 * floor). Email and DM/comment are the conditional rows.
 */
export interface InvitationChannelCapabilities {
  /** The brand knows an email for the recipient: the option is shown. */
  emailAvailable: boolean;
  /** Email is usable: available AND a healthy mailbox is connected. */
  emailEnabled: boolean;
  /** Server-computed: an open DM window on the recipient's social profile. */
  dmAvailable: boolean;
  /** Server-computed: a commentable mention thread exists (independent of DM). */
  commentAvailable: boolean;
  /**
   * The mailbox status is still resolving: the email row stays disabled but
   * keeps its neutral description, without the "not connected" copy or the
   * connect affordance (no wrong-state flash).
   */
  emailStatusPending?: boolean;
}

/** Preselection order: email when usable, then DM, then comment, then the link. */
export const bestAvailableChannel = (c: InvitationChannelCapabilities): InvitationChannelMode =>
  c.emailEnabled ? 'email' : c.dmAvailable ? 'dm' : c.commentAvailable ? 'comment' : 'link';

/** Whether a previously chosen mode is still selectable under the capabilities. */
export const isChannelSelectable = (
  mode: InvitationChannelMode,
  c: InvitationChannelCapabilities
): boolean =>
  mode === 'email'
    ? c.emailEnabled
    : mode === 'dm'
      ? c.dmAvailable
      : mode === 'comment'
        ? c.commentAvailable
        : true;
